import { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";

const BASE = "https://hacker-news.firebaseio.com/v0";

const FEEDS = [
  { key: "topstories",  label: "Top" },
  { key: "newstories",  label: "New" },
  { key: "beststories", label: "Best" },
  { key: "askstories",  label: "Ask" },
  { key: "showstories", label: "Show" },
  { key: "jobstories",  label: "Jobs" },
];

const TOPICS = [
  { id: "ai",          label: "AI & ML",          icon: "🤖", keywords: ["ai","ml","llm","gpt","machine learning","neural","openai","anthropic","deepmind","transformer","diffusion","model","inference","training"] },
  { id: "web",         label: "Web Dev",           icon: "🌐", keywords: ["javascript","typescript","react","vue","svelte","css","html","browser","frontend","backend","node","deno","bun","wasm","webassembly"] },
  { id: "systems",     label: "Systems",           icon: "⚙️", keywords: ["rust","c++","kernel","linux","os","memory","compiler","cpu","hardware","embedded","systems","performance","low-level","assembly"] },
  { id: "security",    label: "Security",          icon: "🔐", keywords: ["security","vulnerability","exploit","cve","hack","breach","zero-day","privacy","encryption","auth","oauth","malware","phishing"] },
  { id: "startups",    label: "Startups",          icon: "🚀", keywords: ["startup","yc","funding","vc","venture","founder","product","launch","saas","b2b","revenue","growth","acquisition","ipo"] },
  { id: "science",     label: "Science",           icon: "🔬", keywords: ["research","paper","study","physics","biology","chemistry","science","nasa","space","climate","quantum","genome","medicine"] },
  { id: "crypto",      label: "Crypto & Web3",     icon: "₿",  keywords: ["bitcoin","ethereum","crypto","blockchain","defi","nft","web3","solana","token","dao","wallet"] },
  { id: "devtools",    label: "Dev Tools",         icon: "🛠️", keywords: ["tool","cli","editor","vscode","neovim","terminal","git","docker","kubernetes","devops","ci","cd","deployment","infra"] },
  { id: "design",      label: "Design & UX",       icon: "🎨", keywords: ["design","ux","ui","figma","typography","color","accessibility","a11y","animation","motion"] },
  { id: "data",        label: "Data & Analytics",  icon: "📊", keywords: ["data","sql","database","postgres","mysql","redis","analytics","bigquery","spark","pandas","dbt","warehouse","pipeline"] },
  { id: "business",    label: "Business & Tech",   icon: "💼", keywords: ["google","apple","microsoft","meta","amazon","tesla","nvidia","openai","regulation","antitrust","acquisition","market"] },
  { id: "open",        label: "Open Source",       icon: "🧩", keywords: ["open source","github","open-source","oss","foss","mit license","apache","community","contributor","fork","pr","release"] },
];

const BENTO_PATTERN = [
  { colSpan: 2, rowSpan: 2 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 1, rowSpan: 2 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 2, rowSpan: 1 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 1, rowSpan: 2 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 2, rowSpan: 1 },
  { colSpan: 1, rowSpan: 1 },
  { colSpan: 1, rowSpan: 1 },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function getSlot(i) { return BENTO_PATTERN[i % BENTO_PATTERN.length]; }

function timeAgo(unix) {
  const d = Math.floor(Date.now() / 1000) - unix;
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function getDomain(url) {
  if (!url) return null;
  try { return new URL(url).hostname.replace("www.", ""); } catch { return null; }
}

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function heatLabel(score, comments) {
  const h = (score ?? 0) + (comments ?? 0) * 2;
  if (h > 800) return { label: "🔥 On Fire",  cls: "heat-fire" };
  if (h > 400) return { label: "⚡ Trending", cls: "heat-hot" };
  if (h > 150) return { label: "📈 Rising",   cls: "heat-rising" };
  return null;
}

function storyMatchesTopics(story, selectedTopics) {
  if (!selectedTopics.length) return true;
  const haystack = `${story.title || ""} ${story.url || ""}`.toLowerCase();
  return selectedTopics.some((tid) => {
    const topic = TOPICS.find((t) => t.id === tid);
    return topic?.keywords.some((kw) => haystack.includes(kw));
  });
}

function storyMatchesSearch(story, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = `${story.title || ""} ${story.url || ""} ${story.by || ""}`.toLowerCase();
  return haystack.includes(q);
}

// ── Welcome Dialog ────────────────────────────────────────────────────────────

function WelcomeDialog({ onDone }) {
  const [step, setStep] = useState(0); // 0 = greeting, 1 = topics
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <div className="welcome-overlay">
      <div className={`welcome-modal step-${step}`}>
        {step === 0 && (
          <div className="welcome-step" key="step0">
            <div className="welcome-logo">
              <span className="logo-mark lg">Y</span>
            </div>
            <h1 className="welcome-title">Welcome to<br />HackerFeed</h1>
            <p className="welcome-sub">
              Your personalised window into Hacker News — filtered by topics you actually care about.
            </p>
            <label className="name-label">
              What should we call you? <span className="optional">(optional)</span>
            </label>
            <input
              className="name-input"
              placeholder="e.g. Alex"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setStep(1)}
              autoFocus
            />
            <button className="welcome-btn primary" onClick={() => setStep(1)}>
              Let's go →
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="welcome-step" key="step1">
            <h2 className="welcome-title sm">
              {name ? `Hey ${name}, pick` : "Pick"} your interests
            </h2>
            <p className="welcome-sub">
              Choose one or more — your feed will surface stories that match.
            </p>

            <div className="topic-grid">
              {TOPICS.map((t) => (
                <button
                  key={t.id}
                  className={`topic-chip${selected.includes(t.id) ? " active" : ""}`}
                  onClick={() => toggle(t.id)}
                >
                  <span className="chip-icon">{t.icon}</span>
                  <span className="chip-label">{t.label}</span>
                  {selected.includes(t.id) && <span className="chip-check">✓</span>}
                </button>
              ))}
            </div>

            <div className="welcome-footer">
              <span className="sel-count">
                {selected.length ? `${selected.length} selected` : "Select at least one, or skip for everything"}
              </span>
              <button
                className="welcome-btn primary"
                onClick={() => onDone({ name, topics: selected })}
              >
                {selected.length ? "Build my feed →" : "Show everything →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Search Bar ────────────────────────────────────────────────────────────────

function SearchBar({ value, onChange, onClear }) {
  const ref = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
      if (e.key === "Escape" && document.activeElement === ref.current) {
        onClear();
        ref.current?.blur();
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [onClear]);

  return (
    <div className="search-wrap">
      <span className="search-icon">⌕</span>
      <input
        ref={ref}
        className="search-input"
        placeholder="Search stories… (⌘K)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button className="search-clear" onClick={onClear}>✕</button>
      )}
    </div>
  );
}

// ── Bento Card ────────────────────────────────────────────────────────────────

function BentoCard({ story, index, onClick, isSelected }) {
  const slot = getSlot(index);
  const isHero = slot.colSpan === 2 && slot.rowSpan === 2;
  const isWide = slot.colSpan === 2 && slot.rowSpan === 1;
  const isTall = slot.colSpan === 1 && slot.rowSpan === 2;

  const style = {
    gridColumn: `span ${slot.colSpan}`,
    gridRow: `span ${slot.rowSpan}`,
    animationDelay: `${Math.min(index * 40, 600)}ms`,
  };

  if (!story) return <div className="bento-card skeleton" style={style} />;

  const domain  = getDomain(story.url);
  const preview = stripHtml(story.text);
  const heat    = heatLabel(story.score, story.descendants);
  const sizeCls = isHero ? "hero" : isWide ? "wide" : isTall ? "tall" : "small";

  return (
    <div
      className={`bento-card size-${sizeCls}${isSelected ? " selected" : ""}`}
      style={style}
      onClick={() => onClick(story)}
    >
      {heat && <div className={`heat-bar ${heat.cls}`} />}
      <div className="card-inner">
        <div className="card-top-row">
          <span className="card-index">{String(index + 1).padStart(2, "0")}</span>
          <div className="card-tags">
            {domain && <span className="tag tag-domain">{domain}</span>}
            {heat
              ? <span className={`tag tag-heat ${heat.cls}`}>{heat.label}</span>
              : <span className="tag tag-type">{story.type}</span>}
          </div>
        </div>
        <h3 className="card-title">{story.title}</h3>
        {(isHero || isTall) && preview && (
          <p className="card-preview">{preview.length > 220 ? preview.slice(0, 220) + "…" : preview}</p>
        )}
        {isWide && preview && (
          <p className="card-preview">{preview.length > 120 ? preview.slice(0, 120) + "…" : preview}</p>
        )}
        <div className="card-meta">
          <span className="meta-author">@{story.by}</span>
          <span className="meta-score">▲ {story.score ?? 0}</span>
          <span className="meta-time">{timeAgo(story.time)}</span>
          {story.descendants != null && (
            <span className="meta-comments">💬 {story.descendants}</span>
          )}
        </div>
      </div>
      {isHero && story.score > 100 && (
        <div className="hero-score-badge">▲{story.score}</div>
      )}
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────────────

function DetailPanel({ story, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!story) return;
    setComments([]);
    if (!story.kids?.length) return;
    setLoading(true);
    Promise.all(
      story.kids.slice(0, 10).map((id) =>
        fetch(`${BASE}/item/${id}.json`).then((r) => r.json())
      )
    )
      .then((d) => setComments(d.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [story]);

  if (!story) return null;
  const heat = heatLabel(story.score, story.descendants);

  return (
    <div className="detail-overlay" onClick={onClose}>
      <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>
        {heat && <div className={`detail-heat-badge ${heat.cls}`}>{heat.label}</div>}
        <div className="detail-header">
          {getDomain(story.url) && <span className="detail-domain">{getDomain(story.url)}</span>}
          <h2 className="detail-title">{story.title}</h2>
          <div className="detail-meta">
            <span className="dm-score">▲ {story.score}</span>
            <span>by {story.by}</span>
            <span>{timeAgo(story.time)}</span>
            {story.descendants != null && <span>💬 {story.descendants}</span>}
          </div>
          <div className="detail-actions">
            {story.url && (
              <a href={story.url} target="_blank" rel="noreferrer" className="btn-primary">Read Article ↗</a>
            )}
            <a href={`https://news.ycombinator.com/item?id=${story.id}`} target="_blank" rel="noreferrer" className="btn-secondary">
              HN Thread
            </a>
          </div>
        </div>
        {story.text && (
          <div className="detail-text" dangerouslySetInnerHTML={{ __html: story.text }} />
        )}
        <div className="comments-section">
          <h4 className="comments-heading">Top Comments</h4>
          {loading && [...Array(4)].map((_, i) => <div key={i} className="comment-skeleton" />)}
          {!loading && comments.length === 0 && <p className="no-comments">No comments yet.</p>}
          {comments.map((c) => (
            <div key={c.id} className="comment">
              <div className="comment-author">{c.by} · {timeAgo(c.time)}</div>
              <div className="comment-text" dangerouslySetInnerHTML={{ __html: c.text || "" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [prefs, setPrefs]       = useState(null); // null = show welcome
  const [feed, setFeed]         = useState("topstories");
  const [ids, setIds]           = useState([]);
  const [stories, setStories]   = useState([]);
  const [page, setPage]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState("");
  const [activeTopics, setActiveTopics] = useState([]); // editable after onboarding
  const PER_PAGE = 30; // fetch more since we filter client-side

  const handleWelcomeDone = ({ name, topics }) => {
    setPrefs({ name });
    setActiveTopics(topics);
  };

  // Fetch IDs when feed changes
  useEffect(() => {
    if (!prefs) return;
    setStories([]);
    setPage(0);
    setSelected(null);
    setLoading(true);
    fetch(`${BASE}/${feed}.json`)
      .then((r) => r.json())
      .then((data) => { setIds(data || []); setLoading(false); });
  }, [feed, prefs]);

  const fetchPage = useCallback((pageNum, currentIds) => {
    const slice = currentIds.slice(pageNum * PER_PAGE, (pageNum + 1) * PER_PAGE);
    if (!slice.length) return;
    setLoading(true);
    setStories((prev) => {
      const next = [...prev];
      slice.forEach((_, i) => { next[pageNum * PER_PAGE + i] = next[pageNum * PER_PAGE + i] || null; });
      return next;
    });
    Promise.all(slice.map((id) => fetch(`${BASE}/item/${id}.json`).then((r) => r.json())))
      .then((data) => {
        setStories((prev) => {
          const next = [...prev];
          data.forEach((s, i) => { next[pageNum * PER_PAGE + i] = s; });
          return next;
        });
        setLoading(false);
      });
  }, []);

  useEffect(() => { if (ids.length) fetchPage(0, ids); }, [ids, fetchPage]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, ids);
  };

  // Filter pipeline: topic filter → search filter
  const allFetched = stories.filter(Boolean);
  const topicFiltered = search
    ? allFetched
    : allFetched.filter((s) => storyMatchesTopics(s, activeTopics));
  const displayed = topicFiltered.filter((s) => storyMatchesSearch(s, search));

  const toggleTopic = (id) =>
    setActiveTopics((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const feedLabel = FEEDS.find((f) => f.key === feed)?.label;
  const hasTopicFilter = activeTopics.length > 0;

  return (
    <div className="app">
      <div className="bg-blob blob1" />
      <div className="bg-blob blob2" />
      <div className="bg-blob blob3" />

      {!prefs && <WelcomeDialog onDone={handleWelcomeDone} />}

      {prefs && (
        <>
          <header className="header">
            <div className="header-inner">
              <div className="logo">
                <span className="logo-mark">Y</span>
                <span className="logo-text">HackerFeed</span>
                {prefs.name && <span className="header-greeting">for {prefs.name}</span>}
              </div>
              <nav className="nav">
                {FEEDS.map((f) => (
                  <button
                    key={f.key}
                    className={`nav-btn${feed === f.key ? " active" : ""}`}
                    onClick={() => { setFeed(f.key); setSearch(""); }}
                  >
                    {f.label}
                  </button>
                ))}
              </nav>
              <SearchBar
                value={search}
                onChange={setSearch}
                onClear={() => setSearch("")}
              />
            </div>
          </header>

          <main className="main">
            {/* Topic filter pills */}
            <div className="topic-filter-bar">
              <span className="filter-label">Topics:</span>
              <div className="filter-chips">
                <button
                  className={`filter-chip${!hasTopicFilter ? " active" : ""}`}
                  onClick={() => setActiveTopics([])}
                >
                  All
                </button>
                {TOPICS.map((t) => (
                  <button
                    key={t.id}
                    className={`filter-chip${activeTopics.includes(t.id) ? " active" : ""}`}
                    onClick={() => toggleTopic(t.id)}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* List header */}
            <div className="list-header">
              {search ? (
                <>
                  <h1 className="list-title">Results for <em>"{search}"</em></h1>
                  <span className="list-count">{displayed.length} found</span>
                </>
              ) : (
                <>
                  <h1 className="list-title">
                    {hasTopicFilter
                      ? `Your ${feedLabel} Feed`
                      : `${feedLabel} Stories`}
                  </h1>
                  <span className="list-count">
                    {hasTopicFilter
                      ? `${displayed.length} matching stories`
                      : `${ids.length} stories`}
                  </span>
                </>
              )}
            </div>

            {/* Empty state */}
            {!loading && displayed.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">🔍</span>
                <p className="empty-title">
                  {search ? "No stories match that search" : "No stories match your topic filters"}
                </p>
                <p className="empty-sub">
                  {search
                    ? "Try different keywords"
                    : "Try selecting more topics or choosing All"}
                </p>
              </div>
            )}

            {/* Bento grid */}
            {displayed.length > 0 && (
              <div className="bento-grid">
                {displayed.map((story, i) => (
                  <BentoCard
                    key={story.id}
                    story={story}
                    index={i}
                    onClick={setSelected}
                    isSelected={selected?.id === story.id}
                  />
                ))}
              </div>
            )}

            {/* Load more — only when not searching */}
            {!search && (page + 1) * PER_PAGE < ids.length && (
              <button className="load-more" onClick={loadMore} disabled={loading}>
                {loading ? "Loading…" : "Load More"}
              </button>
            )}
          </main>

          {selected && <DetailPanel story={selected} onClose={() => setSelected(null)} />}
        </>
      )}
    </div>
  );
}